import { type OperationLink, TRPCClientError, type TRPCLink } from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import { observable, type Unsubscribable } from '@trpc/server/observable';

/**
 * A link whose implementation is imported on first use, so its code stays out of the entry chunk
 * until an operation actually needs it. A failed import is retried by the next operation.
 */
export function lazyLink<TRouter extends AnyRouter>(load: () => Promise<TRPCLink<TRouter>>): TRPCLink<TRouter> {
    return (runtime) => {
        let loading: Promise<OperationLink<TRouter>> | undefined;
        const operationLink = () => {
            loading ??= load()
                .then((link) => link(runtime))
                .catch((error: unknown) => {
                    loading = undefined;
                    throw error;
                });
            return loading;
        };

        return ({ op, next }) =>
            observable((observer) => {
                let subscription: Unsubscribable | undefined;
                let closed = false;
                operationLink().then(
                    (link) => {
                        if (!closed) subscription = link({ op, next }).subscribe(observer);
                    },
                    (cause: unknown) => {
                        if (closed) return;
                        observer.error(
                            TRPCClientError.from<TRouter>(
                                cause instanceof Error ? cause : new Error('The request transport failed to load.'),
                            ),
                        );
                    },
                );
                return () => {
                    closed = true;
                    subscription?.unsubscribe();
                };
            });
    };
}
